/**
 * Tool Store Group (TSG) utilities for consistent TSG key extraction
 * across the application.
 */

/**
 * Extracts the TSG key from a column ID
 * @param {string} colId - Column ID in format 'toolStoreGroups.{tsgKey}.{field}'
 * @returns {string|null} The TSG key or null if not found
 * @example
 * getTsgKeyFromColId('toolStoreGroups.tsg1.selection') // returns 'tsg1'
 */
export const getTsgKeyFromColId = colId => {
  if (!colId) return null;
  if (!colId.startsWith('toolStoreGroups.')) return null;
  const parts = colId.split('.');
  return parts.length >= 2 ? parts[1] : null;
};

/**
 * Extracts the TSG key from a group ID
 * @param {string} groupId - Group ID in format 'toolStoreGroup::{tsgKey}'
 * @returns {string|null} The TSG key or null if not found
 * @example
 * getTsgKeyFromGroupId('toolStoreGroup::tsg1') // returns 'tsg1'
 */
export const getTsgKeyFromGroupId = groupId => {
  if (!groupId) return null;
  if (!groupId.startsWith('toolStoreGroup::')) return null;
  const parts = groupId.split('::');
  return parts.length >= 2 ? parts[1] : null;
};

/**
 * Extracts the TSG key from a column ID using regex
 * This is useful when you need to extract from various formats
 * @param {string} columnId - Column ID containing toolStoreGroups pattern
 * @returns {string|null} The TSG key or null if not found
 * @example
 * getTsgKeyFromColIdRegex('toolStoreGroups.tsg1.competitors.123.price') // returns 'tsg1'
 */
export const getTsgKeyFromColIdRegex = columnId => {
  if (!columnId) return null;
  const match = columnId.match(/toolStoreGroups\.([^.]+)/);
  return match ? match[1] : null;
};

/**
 * Checks if a column ID belongs to a tool store group
 * @param {string} columnId - Column ID to check
 * @returns {boolean} True if the column belongs to a TSG
 */
export const isToolStoreGroupColumn = columnId => {
  if (!columnId) return false;
  return columnId.includes('toolStoreGroups.');
};

/**
 * Checks if a group ID is a tool store group
 * @param {string} groupId - Group ID to check
 * @returns {boolean} True if the group is a TSG
 */
export const isToolStoreGroup = groupId => {
  if (!groupId) return false;
  return groupId.startsWith('toolStoreGroup::');
};

export default {
  getTsgKeyFromColId,
  getTsgKeyFromGroupId,
  getTsgKeyFromColIdRegex,
  isToolStoreGroupColumn,
  isToolStoreGroup,
};
